import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Save, FileDown, FileSpreadsheet, Send, Check, X, GitBranch, Loader2,
  AlertTriangle, ArrowLeft, CloudUpload, CheckCircle2, RotateCcw,
  PanelRightClose, ClipboardList, SlidersHorizontal, Layers, Plus,
  TrendingUp, ListChecks, Lock,
} from 'lucide-react';
import api, { downloadUrl } from '../lib/api';
import { computeProject, buildMastersDict, CATEGORIES, findValidationIssues, findPrefinaliseWarnings } from '../lib/engine';
import { fmtMoney, fmtMoneyLabel } from '../lib/format';
import { MARGIN_BASIS_OPTIONS } from './Settings';
import { CurrencyProvider } from '../context/CurrencyContext';
import { useAuth } from '../context/AuthContext';
import { StatusPill } from '../components/StatusPill';
import { statusLabel } from '../lib/roles';
import { cn } from '../lib/utils';
import { SectionTitle, EmptyState } from '../components/common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import {
  ResizablePanelGroup, ResizablePanel, ResizableHandle,
} from '../components/ui/resizable';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useMediaQuery } from '../hooks/use-media-query';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { SaleBoqPanel } from '../components/workspace/SaleBoqPanel';
import { CostBoqGrid } from '../components/workspace/CostBoqGrid';
import { MarkupWaterfall } from '../components/workspace/MarkupWaterfall';
import { CostMixDonut } from '../components/workspace/CostMixDonut';
import { MarginGauge } from '../components/workspace/MarginGauge';

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `id-${Math.random().toString(36).slice(2)}-${Date.now()}`);
const emptyCategories = () => CATEGORIES.map((name) => ({ name, lines: [] }));

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [draft, setDraft] = useState(null);
  const [masters, setMasters] = useState({});
  const [activities, setActivities] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [vatPct, setVatPct] = useState(0.05);
  const [marginTarget, setMarginTarget] = useState(15);
  const [marginBasis, setMarginBasis] = useState('profit');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [revOpen, setRevOpen] = useState(false);
  const [revForm, setRevForm] = useState({ label: '', note: '' });
  const [statusNote, setStatusNote] = useState('');
  const [statusDialog, setStatusDialog] = useState(null);
  const [focusMode, setFocusMode] = useState(false);
  const [validationIssues, setValidationIssues] = useState(null);
  const [prefinalise, setPrefinalise] = useState(null); // { warnings, status }
  const [outcomeDialog, setOutcomeDialog] = useState(null); // 'Won' | 'Lost'
  const [outcome, setOutcome] = useState({ reason: '', note: '', awardedValue: '', competitor: '' });
  const dirty = useRef(false);
  const undoToastRef = useRef(null);
  const isMobile = useMediaQuery('(max-width: 1023px)');

  const { can } = useAuth();
  const canWrite = can('tenders', 'readwrite');
  const canApprove = can('tenders', 'approve');

  const load = useCallback(async () => {
    const MTYPES = ['material', 'vehicle', 'manpower', 'supervisors', 'tools', 'safety', 'subcontractor', 'misc'];
    const [proj, ...masterRes] = await Promise.all([
      api.get(`/projects/${id}`),
      ...MTYPES.map((t) => api.get(`/masters/${t}`)),
    ]);
    const byType = {};
    MTYPES.forEach((t, i) => { byType[t] = masterRes[i].data; });
    setMasters(buildMastersDict(byType));
    try { const acts = await api.get('/activities'); setActivities(acts.data || []); } catch { /* activities optional */ }
    setProject(proj.data.project);
    setDraft(proj.data.project.draft || { saleItems: [], markup: {} });
    setVatPct(proj.data.settings?.vatPct ?? 0.05);
    setMarginTarget(proj.data.marginTargetPct ?? 15);
    setMarginBasis(proj.data.settings?.marginBasis ?? 'profit');
    setSelectedId((cur) => cur || proj.data.project.draft?.saleItems?.[0]?.itemId || null);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // A Won/locked tender is frozen; its baseline was frozen WITH-K, so the
  // read-only workspace must render the with-K valuation to match the baseline.
  const frozenView = !!(project && (project.locked || project.status === 'Won'));
  const computed = useMemo(
    () => (draft ? computeProject(draft, masters, vatPct, marginBasis, frozenView ? true : null) : null),
    [draft, masters, vatPct, marginBasis, frozenView]
  );

  // K market impact — tender-level cost uplift from the K factor (master direct
  // cost vs with-K direct cost), independent of the Apply-K toggle so managers
  // always see the market impact of the recorded real prices.
  const kImpact = useMemo(() => {
    if (!draft) return null;
    const master = computeProject(draft, masters, vatPct, marginBasis, false);
    const withK = computeProject(draft, masters, vatPct, marginBasis, true);
    const uplift = withK.directCost - master.directCost;
    const pct = master.directCost ? (uplift / master.directCost) * 100 : 0;
    return { masterDirect: master.directCost, kDirect: withK.directCost, uplift, upliftPct: Math.round(pct * 100) / 100 };
  }, [draft, masters, vatPct, marginBasis]);

  // Persist draft (silent autosave, debounced). Read-only users never write.
  const persist = useCallback(async (d) => {
    if (!canWrite) return;
    setSaving(true);
    try {
      await api.put(`/projects/${id}`, { draft: d });
      setSavedAt(new Date());
      dirty.current = false;
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Autosave failed', e); } finally { setSaving(false); }
  }, [id, canWrite]);

  useEffect(() => {
    if (!draft || !dirty.current) return;
    const t = setTimeout(() => persist(draft), 1500);
    return () => clearTimeout(t);
  }, [draft, persist]);

  // ---- Undo (session-level, re-insert based) ----
  const dismissUndo = () => {
    if (undoToastRef.current != null) { toast.dismiss(undoToastRef.current); undoToastRef.current = null; }
  };
  const showUndo = (message, restore) => {
    const tid = toast(message, {
      duration: 8000,
      action: { label: 'Undo', onClick: () => { restore(); } },
      onDismiss: () => { if (undoToastRef.current === tid) undoToastRef.current = null; },
      onAutoClose: () => { if (undoToastRef.current === tid) undoToastRef.current = null; },
    });
    undoToastRef.current = tid;
  };
  // Finalise any pending deletion whenever the user takes another action.
  useEffect(() => () => dismissUndo(), []); // dismiss on unmount

  const mutate = (fn) => {
    if (project && (project.locked || project.status === 'Won')) return; // frozen: no edits
    dismissUndo();
    setDraft((prev) => { const next = structuredClone(prev); fn(next); return next; });
    dirty.current = true;
  };
  // Mutate without finalising the pending undo (used by delete handlers themselves).
  const mutateKeepUndo = (fn) => {
    if (project && (project.locked || project.status === 'Won')) return; // frozen: no edits
    setDraft((prev) => { const next = structuredClone(prev); fn(next); return next; });
    dirty.current = true;
  };

  // ---- Sale item handlers ----
  const addSaleItem = () => {
    const n = (draft.saleItems?.length || 0) + 1;
    const item = { itemId: uid(), itemNo: `S-${String(n).padStart(2, '0')}`, description: '', unit: 'nos', qty: 1, costBoq: { categories: emptyCategories() } };
    mutate((d) => { d.saleItems.push(item); });
    setSelectedId(item.itemId);
  };
  const deleteSaleItem = (itemId) => {
    const idx = draft.saleItems.findIndex((s) => s.itemId === itemId);
    if (idx < 0) return;
    const payload = structuredClone(draft.saleItems[idx]);
    dismissUndo();
    mutateKeepUndo((d) => { d.saleItems.splice(idx, 1); });
    if (selectedId === itemId) setSelectedId(draft.saleItems.find((s) => s.itemId !== itemId)?.itemId || null);
    showUndo(`Deleted ${payload.itemNo} "${payload.description || 'Untitled item'}" · Undo`, () => {
      mutateKeepUndo((d) => { d.saleItems.splice(idx, 0, payload); });
      setSelectedId(payload.itemId);
    });
  };
  const duplicateSaleItem = (itemId) => {
    const idx = draft.saleItems.findIndex((s) => s.itemId === itemId);
    if (idx < 0) return;
    const src = draft.saleItems[idx];
    const nextNum = Math.max(0, ...draft.saleItems.map((s) => parseInt((s.itemNo || '').replace(/\D/g, ''), 10) || 0)) + 1;
    const copy = structuredClone(src);
    copy.itemId = uid();
    copy.itemNo = `S-${String(nextNum).padStart(2, '0')}`;
    copy.description = `${src.description || ''} (Copy)`.trim();
    (copy.costBoq?.categories || []).forEach((cat) => (cat.lines || []).forEach((ln) => { ln.lineId = uid(); }));
    mutate((d) => { d.saleItems.splice(idx + 1, 0, copy); });
    setSelectedId(copy.itemId);
    toast.success(`Duplicated ${src.itemNo} → ${copy.itemNo}`);
  };
  const setQty = (itemId, qty) => mutate((d) => {
    const si = d.saleItems.find((s) => s.itemId === itemId);
    if (si) si.qty = Math.max(1, parseInt(qty, 10) || 1);
  });
  // P5 — per-line Profit %. Stored as a decimal (0.15 == 15%) on the sale item.
  const setLineProfit = (itemId, pct) => mutate((d) => {
    const si = d.saleItems.find((s) => s.itemId === itemId);
    if (si) si.profitPct = Math.max(0, parseFloat(pct) || 0) / 100;
  });
  const setItemField = (itemId, field, val) => mutate((d) => {
    const si = d.saleItems.find((s) => s.itemId === itemId); if (si) si[field] = val;
  });

  // ---- Cost line handlers ----
  const findCat = (d, catName) => d.saleItems.find((s) => s.itemId === selectedId)?.costBoq.categories.find((c) => c.name === catName);
  const addLine = (catName) => mutate((d) => {
    const cat = findCat(d, catName);
    cat.lines.push({ lineId: uid(), code: '', description: '', unit: '', qtyPerUnit: 1, rate: 0 });
  });
  const changeLine = (catName, lineId, field, value) => mutate((d) => {
    const cat = findCat(d, catName); const ln = cat.lines.find((l) => l.lineId === lineId);
    if (!ln) return;
    if (field === 'qtyPerUnit' || field === 'rate' || field === 'kValue') {
      if (value === '') { ln[field] = field === 'kValue' ? null : ''; }
      else { const n = Number(value); ln[field] = Number.isNaN(n) ? ln[field] : Math.max(0, n); }
    } else {
      ln[field] = value;
    }
  });
  const deleteLine = (catName, lineId) => {
    const ci = draft.saleItems.find((s) => s.itemId === selectedId);
    const cat = ci?.costBoq.categories.find((c) => c.name === catName);
    const idx = cat ? cat.lines.findIndex((l) => l.lineId === lineId) : -1;
    if (idx < 0) return;
    const payload = structuredClone(cat.lines[idx]);
    const itemId = selectedId;
    dismissUndo();
    mutateKeepUndo((d) => {
      const c = d.saleItems.find((s) => s.itemId === itemId)?.costBoq.categories.find((x) => x.name === catName);
      if (c) c.lines.splice(idx, 1);
    });
    showUndo(`Deleted "${payload.description || payload.code || 'line item'}" · Undo`, () => {
      mutateKeepUndo((d) => {
        const c = d.saleItems.find((s) => s.itemId === itemId)?.costBoq.categories.find((x) => x.name === catName);
        if (c) c.lines.splice(idx, 0, payload);
      });
    });
  };
  const duplicateLine = (catName, lineId) => mutate((d) => {
    const cat = findCat(d, catName); const idx = cat.lines.findIndex((l) => l.lineId === lineId);
    if (idx >= 0) cat.lines.splice(idx + 1, 0, { ...structuredClone(cat.lines[idx]), lineId: uid() });
  });
  // ---- Bulk (multi-select) handlers ----
  const bulkCopyLines = (items) => mutate((d) => {
    const ci = d.saleItems.find((s) => s.itemId === selectedId);
    const byCat = {};
    items.forEach((i) => { (byCat[i.catName] = byCat[i.catName] || []).push(i.lineId); });
    Object.entries(byCat).forEach(([cn, ids]) => {
      const cat = ci.costBoq.categories.find((c) => c.name === cn);
      if (!cat) return;
      const sel = cat.lines.filter((l) => ids.includes(l.lineId));
      let maxIdx = -1;
      cat.lines.forEach((l, i) => { if (ids.includes(l.lineId)) maxIdx = i; });
      const copies = sel.map((l) => ({ ...structuredClone(l), lineId: uid() }));
      cat.lines.splice(maxIdx + 1, 0, ...copies);
    });
  });
  const bulkDeleteLines = (items) => {
    const itemId = selectedId;
    const ci = draft.saleItems.find((s) => s.itemId === itemId);
    if (!ci) return;
    const caps = [];
    items.forEach(({ catName, lineId }) => {
      const cat = ci.costBoq.categories.find((c) => c.name === catName);
      const idx = cat ? cat.lines.findIndex((l) => l.lineId === lineId) : -1;
      if (idx >= 0) caps.push({ catName, idx, payload: structuredClone(cat.lines[idx]) });
    });
    if (!caps.length) return;
    const ids = new Set(items.map((i) => i.lineId));
    dismissUndo();
    mutateKeepUndo((d) => {
      const c = d.saleItems.find((s) => s.itemId === itemId);
      c.costBoq.categories.forEach((cat) => { cat.lines = cat.lines.filter((l) => !ids.has(l.lineId)); });
    });
    showUndo(`Deleted ${caps.length} item${caps.length > 1 ? 's' : ''} · Undo`, () => {
      mutateKeepUndo((d) => {
        const c = d.saleItems.find((s) => s.itemId === itemId);
        const byCat = {};
        caps.forEach((cap) => { (byCat[cap.catName] = byCat[cap.catName] || []).push(cap); });
        Object.entries(byCat).forEach(([cn, arr]) => {
          const cat = c.costBoq.categories.find((x) => x.name === cn);
          if (cat) arr.sort((a, b) => a.idx - b.idx).forEach((cap) => cat.lines.splice(cap.idx, 0, cap.payload));
        });
      });
    });
  };
  const pasteRows = (catName, rows) => mutate((d) => {
    const cat = findCat(d, catName);
    rows.forEach((r) => cat.lines.push({ lineId: uid(), ...r }));
  });

  // ---- Activity Master: apply / detach on the selected sale line ----
  const applyActivity = (activity) => {
    const instanceId = uid();
    mutate((d) => {
      const ci = d.saleItems.find((s) => s.itemId === selectedId);
      if (!ci) return;
      const ensureCat = (name) => {
        let cat = ci.costBoq.categories.find((c) => c.name === name);
        if (!cat) { cat = { name, lines: [] }; ci.costBoq.categories.push(cat); }
        return cat;
      };
      (activity.composition || []).forEach((comp) => {
        const cat = ensureCat(comp.category);
        cat.lines.push({
          lineId: uid(), code: comp.rateCode, description: '', unit: '',
          qtyPerUnit: comp.qtyPerUnit, rate: 0,
          activity: { instanceId, activityId: activity.id, code: activity.activityCode, name: activity.description },
        });
      });
    });
    toast.success(`Applied "${activity.description}" — ${(activity.composition || []).length} locked row(s) added`);
  };
  const detachActivity = (instanceId) => {
    mutate((d) => {
      const ci = d.saleItems.find((s) => s.itemId === selectedId);
      if (!ci) return;
      ci.costBoq.categories.forEach((cat) => {
        cat.lines = cat.lines.filter((l) => !(l.activity && l.activity.instanceId === instanceId));
      });
    });
    toast.success('Activity detached — its rows were cleared');
  };

  const setMarkup = (key, val) => mutate((d) => { d.markup[key] = (parseFloat(val) || 0) / 100; });

  // K factor — tender-level "Apply K" toggle (market-price view for MATERIAL lines).
  const toggleApplyK = (on) => mutate((d) => { d.applyK = !!on; });

  // ---- Revisions / status / export ----
  const saveRevision = async () => {
    dismissUndo();
    await persist(draft);
    try {
      const { data } = await api.post(`/projects/${id}/revisions`, revForm);
      toast.success(`Rev ${data.revNo} created`);
      setRevOpen(false); setRevForm({ label: '', note: '' });
      load();
    } catch { toast.error('Could not create revision'); }
  };

  const belowTarget = computed && computed.marginPct < marginTarget;

  const changeStatus = async (status, { ack = false, outcomeData = null } = {}) => {
    dismissUndo();
    // Block progression of bids that contain invalid (negative) values.
    if (['SubmittedToManager', 'SubmittedToAdmin', 'Approved', 'Sent'].includes(status)) {
      const issues = findValidationIssues(draft);
      if (issues.length) {
        setValidationIssues(issues);
        toast.error('This bid cannot be sent for approval — fix the highlighted values.');
        return;
      }
    }
    // Win/Loss outcome capture (Prompt 3): a reason is mandatory.
    if ((status === 'Won' || status === 'Lost') && !outcomeData) {
      setOutcome({ reason: '', note: '', awardedValue: '', competitor: '' });
      setOutcomeDialog(status);
      return;
    }
    // Pre-finalise checklist (B4): must acknowledge soft warnings to move past Draft.
    if (status === 'SubmittedToManager' && !ack) {
      const warns = findPrefinaliseWarnings(computed);
      if (warns.length) { setPrefinalise({ warnings: warns, status }); return; }
    }
    if (status === 'SubmittedToManager' && belowTarget && !statusNote.trim()) {
      setStatusDialog('SubmittedToManager'); return;
    }
    try {
      await api.post(`/projects/${id}/status`, { status, note: statusNote, acknowledge: ack, outcome: outcomeData });
      toast.success(`Moved to ${statusLabel(status)}`);
      setStatusDialog(null); setStatusNote(''); setPrefinalise(null); setOutcomeDialog(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 422 && detail?.issues) {
        setValidationIssues(detail.issues);
        toast.error(detail.message || 'Estimate contains invalid values.');
      } else if (e.response?.status === 428 && detail?.warnings) {
        setPrefinalise({ warnings: detail.warnings, status });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Status change failed');
      }
    }
  };

  if (!project || !draft || !computed) {
    return <div className="grid h-[calc(100vh-4rem)] place-items-center"><Loader2 className="animate-spin text-muted-foreground" /></div>;
  }

  const selected = computed.saleItems.find((s) => s.itemId === selectedId) || computed.saleItems[0];
  const rawSelected = draft.saleItems.find((s) => s.itemId === selectedId) || draft.saleItems[0];
  const status = project.status;
  const cur = project.currency;
  // A Won tender is frozen into an immutable baseline — read-only everywhere.
  const frozen = !!project.locked || status === 'Won';

  const saleBoqPane = (
    <SaleBoqPanel computed={computed} selectedId={selectedId} onSelect={setSelectedId} readOnly={frozen}
      onQtyChange={setQty} onProfitChange={setLineProfit} onAddItem={addSaleItem} onDeleteItem={deleteSaleItem} onDuplicateItem={duplicateSaleItem} />
  );

  const costEditorPane = (
    selected ? (
      <div className="flex h-full flex-col">
        {frozen && (
          <div data-testid="workspace-frozen-banner" className="flex items-center gap-2 border-b bg-[hsl(var(--warning)/0.12)] px-4 py-2 text-xs font-medium text-[hsl(var(--warning))]">
            <Lock size={13} className="shrink-0" />
            This tender is Won and frozen to an immutable baseline. The estimate is read-only — make changes via the Project budget revision flow.
          </div>
        )}
        {/* editable description + compact totals (focus mode keeps key numbers visible) */}
        <div className="flex items-center gap-3 border-b bg-muted/20 px-4 py-2">
          <input value={rawSelected.description ?? ''} disabled={frozen} onChange={(e) => setItemField(selectedId, 'description', e.target.value)}
            placeholder="Sale item description…" className="grid-input min-w-0 flex-1 text-sm" />
          {focusMode && (
            <div className="hidden shrink-0 items-center gap-4 lg:flex">
              <div className="text-right"><div className="text-[9px] uppercase text-muted-foreground">Item Cost</div><div className="money text-xs font-semibold">{fmtMoney(selected.totalCost, cur, 0)}</div></div>
              <div className="text-right"><div className="text-[9px] uppercase text-muted-foreground">Eff. Unit</div><div className="money text-xs font-semibold">{fmtMoney(selected.effectiveUnitCost, cur, 0)}</div></div>
              <div className="text-right"><div className="text-[9px] uppercase text-muted-foreground">Quotation</div><div className="money text-xs font-semibold text-[hsl(var(--copper))]">{fmtMoney(computed.quotationPrice, cur, 0)}</div></div>
            </div>
          )}
        </div>
        <div className="min-h-0 flex-1">
          <CostBoqGrid saleItem={selected} rawSaleItem={rawSelected} masters={masters} wide={focusMode} readOnly={frozen}
            projectDirectCost={computed.directCost}
            applyK={frozen ? true : !!draft.applyK} onToggleApplyK={toggleApplyK}
            activities={activities} onApplyActivity={applyActivity} onDetachActivity={detachActivity}
            onChange={changeLine} onAddLine={addLine} onDeleteLine={deleteLine}
            onDuplicateLine={duplicateLine} onPasteRows={pasteRows}
            onBulkCopy={bulkCopyLines} onBulkDelete={bulkDeleteLines} />
        </div>
      </div>
    ) : (
      <div className="grid h-full place-items-center p-6">
        <EmptyState
          testid="workspace-no-items-empty"
          icon={Layers}
          title="No sale item selected"
          description="Add your first Sale BOQ line to start building its cost breakdown across material, labour and resources."
          action={<Button onClick={addSaleItem} className="bg-[hsl(var(--brand))] text-white hover:brightness-110"><Plus size={15} className="mr-2" />Add Sale Item</Button>}
          compact
        />
      </div>
    )
  );

  const pricingPane = (
    <div data-testid="workspace-pricing-panel" className="h-full overflow-y-auto p-3 space-y-4">
      {belowTarget && (
        <div className="flex items-start gap-2 rounded-xl border border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.12)] p-2.5 text-xs text-[hsl(var(--warning))]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>Margin {computed.marginPct}% is below the {marginTarget}% target. A note is required to submit.</span>
        </div>
      )}
      {computed.unresolvedCount > 0 && (
        <div className="rounded-xl border border-[hsl(var(--danger)/0.4)] bg-[hsl(var(--danger)/0.1)] p-2.5 text-xs text-[hsl(var(--danger))]">
          {computed.unresolvedCount} cost line(s) reference an unknown/inactive code.
        </div>
      )}

      {/* K market impact — cost uplift from the K factor (master vs real-price direct cost) */}
      {kImpact && kImpact.uplift !== 0 && (
        <div data-testid="k-impact-card" className="rounded-xl border border-[hsl(var(--copper)/0.4)] bg-[hsl(var(--copper)/0.08)] p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-[hsl(var(--copper))]">
              <TrendingUp size={14} />K Market Impact
            </div>
            <span data-testid="k-impact-pct" className="money text-xs font-semibold text-[hsl(var(--copper))]">
              {kImpact.uplift > 0 ? '+' : ''}{kImpact.upliftPct}%
            </span>
          </div>
          <div data-testid="k-impact-uplift" className="money mt-0.5 text-lg font-bold text-[hsl(var(--copper))]">
            {kImpact.uplift > 0 ? '+' : ''}{fmtMoneyLabel(kImpact.uplift, cur)}
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <div className="text-muted-foreground">Master direct</div>
              <div data-testid="k-impact-master" className="money font-medium">{fmtMoney(kImpact.masterDirect, cur)}</div>
            </div>
            <div>
              <div className="text-muted-foreground">K · real-price direct</div>
              <div data-testid="k-impact-k" className="money font-medium">{fmtMoney(kImpact.kDirect, cur)}</div>
            </div>
          </div>
          <div className="mt-1.5 text-[10px] text-muted-foreground">
            Direct-cost uplift from real market prices{computed.applyK ? '' : ' — not currently applied (toggle Apply K on the Cost BOQ)'}.
          </div>
        </div>
      )}

      <div>
        <SectionTitle className="mb-3">Markup Ladder</SectionTitle>
        <MarkupWaterfall computed={computed} markup={draft.markup} onMarkupChange={setMarkup} readOnly={frozen} />
        <div className="mt-3 flex items-center justify-between rounded-xl border bg-muted/30 px-3 py-2 text-xs">
          <span className="text-muted-foreground">VAT ({(vatPct * 100).toFixed(0)}%) · Grand Total</span>
          <span className="money font-semibold">{fmtMoneyLabel(computed.grandTotal, cur)}</span>
        </div>
      </div>

      <div>
        <SectionTitle className="mb-2">Margin</SectionTitle>
        <MarginGauge marginPct={computed.marginPct} target={marginTarget}
          basisLabel={(MARGIN_BASIS_OPTIONS.find((o) => o.value === computed.marginBasis) || {}).short} />
        {/* Net Profit (G) — fixed definition, after negotiation margin */}
        <div data-testid="net-profit-card" className="mt-3 rounded-xl border bg-[hsl(var(--success)/0.08)] p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
              <TrendingUp size={14} className="text-[hsl(var(--success))]" />Net Profit
            </div>
            <span data-testid="net-profit-pct" className="money text-xs font-semibold">{computed.netProfitPct}%</span>
          </div>
          <div data-testid="net-profit-value" className="money mt-0.5 text-lg font-bold text-[hsl(var(--success))]">{fmtMoneyLabel(computed.netProfit, cur)}</div>
          <div className="text-[10px] text-muted-foreground">Net Profit (after negotiation margin)</div>
        </div>
      </div>

      <div>
        <SectionTitle className="mb-2">Cost Mix</SectionTitle>
        <CostMixDonut costMix={computed.costMix} />
      </div>

      {/* Actions */}
      <div className="space-y-2 border-t pt-4">
        {!frozen && (
          <Button data-testid="save-revision-button" onClick={() => setRevOpen(true)} className="w-full bg-[hsl(var(--brand))] text-white hover:brightness-110">
            <Save size={15} className="mr-2" />Save Revision
          </Button>
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" data-testid="workspace-export-quotation-button" onClick={() => window.open(downloadUrl(`/projects/${id}/quotation.pdf`), '_blank')}><FileDown size={15} className="mr-1.5" />PDF</Button>
          <Button variant="outline" onClick={() => window.open(downloadUrl(`/projects/${id}/quotation.xlsx`), '_blank')}><FileSpreadsheet size={15} className="mr-1.5" />Excel</Button>
        </div>
        {/* Status workflow — Estimator -> Estimation Manager -> Admin */}
        {status === 'Draft' && canWrite && (
          <Button variant="outline" className="w-full" data-testid="submit-approval-button" onClick={() => changeStatus('SubmittedToManager')}>
            <Send size={15} className="mr-2" />Submit for Approval
          </Button>
        )}
        {status === 'SubmittedToManager' && canApprove && (
          <div className="space-y-2">
            <Button data-testid="forward-to-admin-button" className="w-full bg-[hsl(var(--success))] text-white hover:brightness-95" onClick={() => changeStatus('SubmittedToAdmin')}>
              <Check size={15} className="mr-1.5" />Approve &amp; Send to Admin
            </Button>
            <Button variant="outline" className="w-full text-[hsl(var(--danger))]" onClick={() => setStatusDialog('Rejected')}><X size={15} className="mr-1.5" />Reject to Estimator</Button>
          </div>
        )}
        {status === 'SubmittedToManager' && !canApprove && (
          <div className="rounded-xl border bg-muted/30 py-2.5 text-center text-xs text-muted-foreground">Pending Estimation Manager review.</div>
        )}
        {status === 'SubmittedToAdmin' && canApprove && (
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="admin-approve-button" className="bg-[hsl(var(--success))] text-white hover:brightness-95" onClick={() => changeStatus('Approved')}><Check size={15} className="mr-1.5" />Approve</Button>
            <Button variant="outline" className="text-[hsl(var(--danger))]" onClick={() => setStatusDialog('Rejected')}><X size={15} className="mr-1.5" />Reject</Button>
          </div>
        )}
        {status === 'SubmittedToAdmin' && !canApprove && (
          <div className="rounded-xl border bg-muted/30 py-2.5 text-center text-xs text-muted-foreground">Pending Admin approval.</div>
        )}
        {status === 'Rejected' && (
          <Button variant="outline" className="w-full" onClick={() => changeStatus('Draft')}><RotateCcw size={15} className="mr-2" />Return to Draft</Button>
        )}
        {status === 'Approved' && canApprove && <Button variant="outline" className="w-full" onClick={() => changeStatus('Sent')}><Send size={15} className="mr-2" />Mark as Sent</Button>}
        {status === 'Sent' && canApprove && (
          <div className="grid grid-cols-2 gap-2">
            <Button data-testid="mark-won-button" className="bg-[hsl(var(--success))] text-white" onClick={() => changeStatus('Won')}>Won</Button>
            <Button data-testid="mark-lost-button" variant="outline" onClick={() => changeStatus('Lost')}>Lost</Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <CurrencyProvider currency={cur}>
    <div className="flex h-[calc(100vh-4rem)] flex-col">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 border-b px-4 py-2.5">
        <button onClick={() => navigate('/projects')} className="grid h-8 w-8 place-items-center rounded-lg border hover:bg-muted"><ArrowLeft size={15} /></button>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-num text-xs text-[hsl(var(--copper))]">{project.code}</span>
            <input value={project.name} onChange={(e) => setProject({ ...project, name: e.target.value })}
              onBlur={(e) => api.put(`/projects/${id}`, { name: e.target.value })}
              className="grid-input max-w-xs font-display text-sm font-semibold" />
          </div>
          <div className="text-[11px] text-muted-foreground">{project.client} · {project.location}</div>
        </div>
        <StatusPill status={status} />
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          {saving ? <span className="flex items-center gap-1"><CloudUpload size={13} className="animate-pulse" />Saving…</span>
            : savedAt ? <span className="flex items-center gap-1 text-[hsl(var(--success))]"><CheckCircle2 size={13} />Saved</span> : null}
          {/* View mode toggle */}
          <div className="hidden items-center rounded-lg border p-0.5 lg:flex">
            <button data-testid="view-mode-estimate" onClick={() => setFocusMode(false)}
              className={cn('flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                !focusMode ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:bg-muted')}>
              <SlidersHorizontal size={13} />Estimate
            </button>
            <button data-testid="view-mode-cost-editor" onClick={() => setFocusMode(true)}
              className={cn('flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                focusMode ? 'bg-[hsl(var(--primary))] text-white' : 'text-muted-foreground hover:bg-muted')}>
              <PanelRightClose size={13} />Cost Editor
            </button>
          </div>
          <Button variant="outline" size="sm" data-testid="bid-summary-button" onClick={() => navigate(`/projects/${id}/bid-summary`)}><ClipboardList size={14} className="mr-1.5 sm:mr-1.5" /><span className="hidden sm:inline">Bid Summary</span></Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/projects/${id}/revisions`)}><GitBranch size={14} className="sm:mr-1.5" /><span className="hidden sm:inline">Revisions</span></Button>
        </div>
      </div>

      {isMobile ? (
        /* Mobile: stacked tabbed layout */
        <Tabs defaultValue="cost" className="flex min-h-0 flex-1 flex-col">
          <TabsList className="mx-3 mt-3 grid grid-cols-3" data-testid="workspace-mobile-tabs">
            <TabsTrigger value="items" data-testid="workspace-mobile-tab-items">Sale BOQ</TabsTrigger>
            <TabsTrigger value="cost" data-testid="workspace-mobile-tab-cost">Cost Editor</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="workspace-mobile-tab-pricing">Pricing</TabsTrigger>
          </TabsList>
          <TabsContent value="items" className="min-h-0 flex-1 overflow-hidden border-t">{saleBoqPane}</TabsContent>
          <TabsContent value="cost" className="min-h-0 flex-1 overflow-hidden border-t">{costEditorPane}</TabsContent>
          <TabsContent value="pricing" className="min-h-0 flex-1 overflow-hidden border-t bg-card">{pricingPane}</TabsContent>
        </Tabs>
      ) : (
        /* Desktop: Sale BOQ + Cost Editor are resizable; the Markup Ladder / pricing
           panel is a FIXED-WIDTH column (no drag-resize). */
        <div className="flex min-h-0 flex-1">
          <ResizablePanelGroup key={focusMode ? 'focus' : 'full'} direction="horizontal" className="min-w-0 flex-1">
            <ResizablePanel defaultSize={focusMode ? 22 : 32} minSize={16} className="bg-card">
              {saleBoqPane}
            </ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={focusMode ? 78 : 68} minSize={30}>
              {costEditorPane}
            </ResizablePanel>
          </ResizablePanelGroup>
          {!focusMode && (
            <div className="w-[300px] shrink-0 border-l bg-card" data-testid="workspace-pricing-column">
              {pricingPane}
            </div>
          )}
        </div>
      )}

      {/* Validation issues popup — blocks sending an invalid bid for approval */}
      <Dialog open={!!validationIssues} onOpenChange={(o) => !o && setValidationIssues(null)}>
        <DialogContent data-testid="validation-issues-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[hsl(var(--danger))]">
              <AlertTriangle size={18} />Cannot send for approval
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">This estimate contains invalid values. Please correct the following before submitting:</p>
          <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-xl border bg-[hsl(var(--danger)/0.06)] p-3">
            {(validationIssues || []).map((iss, i) => (
              <div key={`${i}-${iss}`} className="flex items-start gap-2 text-xs text-[hsl(var(--danger))]">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-current" />{iss}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setValidationIssues(null)}>Got it</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-finalise checklist (B4) — must acknowledge soft warnings to submit */}
      <Dialog open={!!prefinalise} onOpenChange={(o) => !o && setPrefinalise(null)}>
        <DialogContent data-testid="prefinalise-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[hsl(var(--warning))]">
              <ListChecks size={18} />Pre-finalise checklist
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground">
            Please review the following before submitting this tender for approval. You can go back to fix them, or acknowledge and submit anyway.
          </p>
          <div className="max-h-64 space-y-1.5 overflow-y-auto rounded-xl border bg-[hsl(var(--warning)/0.08)] p-3">
            {(prefinalise?.warnings || []).map((w, i) => (
              <div key={`${i}-${w}`} className="flex items-start gap-2 text-xs text-[hsl(var(--warning))]" data-testid={`prefinalise-warning-${i}`}>
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />{w}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrefinalise(null)}>Go back &amp; fix</Button>
            <Button data-testid="prefinalise-acknowledge" className="bg-[hsl(var(--warning))] text-white hover:brightness-95"
              onClick={() => changeStatus(prefinalise.status, { ack: true })}>
              Acknowledge &amp; Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save revision dialog */}
      <Dialog open={revOpen} onOpenChange={setRevOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Save Revision</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Creates an immutable snapshot of the current Sale + Cost BOQ and pricing.</p>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Label</Label><Input data-testid="revision-label-input" value={revForm.label} onChange={(e) => setRevForm({ ...revForm, label: e.target.value })} placeholder="e.g. Updated cable lengths" /></div>
            <div className="space-y-1.5"><Label>Note</Label><Textarea rows={3} value={revForm.note} onChange={(e) => setRevForm({ ...revForm, note: e.target.value })} placeholder="What changed and why" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevOpen(false)}>Cancel</Button>
            <Button data-testid="confirm-save-revision" onClick={saveRevision}>Create Revision</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status note dialog (guardrail / reject) */}
      <Dialog open={!!statusDialog} onOpenChange={(o) => !o && setStatusDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{statusDialog === 'Rejected' ? 'Reject quotation' : 'Submit with note'}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">{statusDialog === 'SubmittedToManager' ? `Margin is below the ${marginTarget}% target — please add a justification note.` : 'Add a comment for the record.'}</p>
          <Textarea rows={3} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="Note…" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(null)}>Cancel</Button>
            <Button onClick={() => changeStatus(statusDialog, { ack: true })} disabled={!statusNote.trim()}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Win / Loss outcome capture (Prompt 3) */}
      <Dialog open={!!outcomeDialog} onOpenChange={(o) => !o && setOutcomeDialog(null)}>
        <DialogContent data-testid="outcome-dialog">
          <DialogHeader><DialogTitle>Mark tender as {outcomeDialog}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Capture why this tender was {outcomeDialog?.toLowerCase()} — this feeds the Win/Loss analytics.</p>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Reason <span className="text-[hsl(var(--danger))]">*</span></Label>
              <Select value={outcome.reason} onValueChange={(v) => setOutcome({ ...outcome, reason: v })}>
                <SelectTrigger data-testid="outcome-reason-select"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {['Price', 'Technical', 'Delivery', 'Relationship', 'Scope', 'No-bid', 'Other'].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{outcomeDialog === 'Won' ? 'Awarded value' : 'Competitor value'} ({cur})</Label>
                <Input type="number" min="0" data-testid="outcome-awarded-input" value={outcome.awardedValue}
                  onChange={(e) => setOutcome({ ...outcome, awardedValue: e.target.value })} placeholder="Optional" className="font-num" /></div>
              <div className="space-y-1.5"><Label>Competitor</Label>
                <Input data-testid="outcome-competitor-input" value={outcome.competitor}
                  onChange={(e) => setOutcome({ ...outcome, competitor: e.target.value })} placeholder="Optional" /></div>
            </div>
            <div className="space-y-1.5"><Label>Note</Label>
              <Textarea rows={2} data-testid="outcome-note-input" value={outcome.note}
                onChange={(e) => setOutcome({ ...outcome, note: e.target.value })} placeholder="Optional context" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOutcomeDialog(null)}>Cancel</Button>
            <Button data-testid="outcome-confirm" disabled={!outcome.reason}
              onClick={() => changeStatus(outcomeDialog, { outcomeData: outcome })}>Confirm {outcomeDialog}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </CurrencyProvider>
  );
}
